import React, { useState, useMemo, useEffect, useCallback } from 'react';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useTheme } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getClientRequests, removeClientRequest } from '../../../store/slices/clientsSlice';
import { getUser } from '../../../store/slices/userSlice';
import { getJobs, updateJob } from '../../../store/slices/jobsSlice';
import { getUsersList } from '../../../store/slices/usersSlice';
import { formatUserDisplayName, resolveUserDisplayNameByEmail, resolveUserDisplayNameById } from '../../../utils/userDisplayName';
import AcceptClientRequestDialog from './AcceptClientRequestDialog';
import RejectClientRequestDialog from './RejectClientRequestDialog';
import JobDetailsDialog from '../jobs/JobDetailsDialog';
import DismissJobUnlockRequestDialog from '../jobs/DismissJobUnlockRequestDialog';
import UnlockJobConfirmDialog from '../jobs/UnlockJobConfirmDialog';
import RequestsSection from './RequestsSection';
import RequestsActionButtons from './RequestsActionButtons';
import {
  DEFAULT_GRID_PREFERENCE,
  DEFAULT_SECTION_HEIGHT,
  GRID_PREFERENCES_STORAGE_KEY,
  SECTION_HEIGHTS_STORAGE_KEY,
  clampSectionHeight,
  parseGridPreferences,
  parseSectionHeights,
  sanitizeGridPreference,
} from './RequestsColumnsHelper';
import {
  deriveJobTechnicianRequestRows,
  deriveProposalRequestRows,
  derivePartRequestRows,
  deriveJobUnlockRequestRows,
  deriveMyRequestRows,
  getJobTechnicianChipStyles,
} from '../../../utils/jobTechnicianRequests';
import { routes, api } from '../../../utils/constants';
import { GET, PATCH, POST } from '../../../configs/api';
import {
  isManagement,
  canAccessRequestsPage,
  canAccessPartRequestsTab,
  canAccessProposalsTab,
  canAccessJobUnlockRequestsTab,
} from '../../../utils/permissions';
import { getStatusLabel, getStatusChipStyles } from '../../../utils/jobStatuses';
import { getClientChipStyles } from '../../../utils/clientColors';
import useIsMobile from '../../../hooks/useIsMobile';

const Requests = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const clientRequests = useSelector(getClientRequests);
  const user = useSelector(getUser);
  const { jobs } = useSelector(getJobs);
  const usersList = useSelector(getUsersList);

  const displayUserByEmail = useMemo(() => {
    const byEmail = new Map(
      (usersList || [])
        .filter((u) => u.email)
        .map((u) => [u.email.toLowerCase(), u])
    );
    return (email) => {
      if (!email) return '';
      const match = byEmail.get(String(email).toLowerCase());
      return match ? formatUserDisplayName(match) : email;
    };
  }, [usersList]);
  const [acceptClientRequestDialogOpen, setAcceptClientRequestDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [myRequestsSearchQuery, setMyRequestsSearchQuery] = useState('');
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [jobUnlockSearchQuery, setJobUnlockSearchQuery] = useState('');
  const [dismissUnlockRow, setDismissUnlockRow] = useState(null);
  const [dismissUnlockReason, setDismissUnlockReason] = useState('');
  const [dismissingUnlockRequest, setDismissingUnlockRequest] = useState(false);
  const [unlockJobRow, setUnlockJobRow] = useState(null);
  const [unlockingJob, setUnlockingJob] = useState(false);
  const [rejectClientRequestRow, setRejectClientRequestRow] = useState(null);
  const [rejectClientRequestReason, setRejectClientRequestReason] = useState('');
  const [rejectingClientRequest, setRejectingClientRequest] = useState(false);
  const [dismissFeedback, setDismissFeedback] = useState({ open: false, severity: 'success', message: '' });
  const [technicianSearchQuery, setTechnicianSearchQuery] = useState('');
  const [proposalSearchQuery, setProposalSearchQuery] = useState('');
  const [partRequestSearchQuery, setPartRequestSearchQuery] = useState('');
  // State for embedded JobDetailsDialog overlay
  const [selectedJobForDetails, setSelectedJobForDetails] = useState(null);
  // State for initial tab when opening JobDetailsDialog (for proposals grid)
  const [initialTabValue, setInitialTabValue] = useState(1);
  
  // Load expanded sections state from localStorage
  const loadExpandedSections = () => {
    try {
      const saved = localStorage.getItem('requestsSectionsExpanded');
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          myRequests: parsed.myRequests !== undefined ? parsed.myRequests : true,
          clientRequests: parsed.clientRequests !== undefined ? parsed.clientRequests : true,
          jobUnlockRequests: parsed.jobUnlockRequests !== undefined ? parsed.jobUnlockRequests : true,
          technicianRequests: parsed.technicianRequests !== undefined ? parsed.technicianRequests : true,
          partRequests: parsed.partRequests !== undefined ? parsed.partRequests : true,
          proposalRequests: parsed.proposalRequests !== undefined ? parsed.proposalRequests : true,
        };
      }
    } catch (error) {
      console.error('Error loading expanded sections from localStorage:', error);
    }
    // Default: all expanded
    return {
      myRequests: true,
      clientRequests: true,
      jobUnlockRequests: true,
      technicianRequests: true,
      partRequests: true,
      proposalRequests: true,
    };
  };

  // State for expanded sections - initialized from localStorage
  const [expandedSections, setExpandedSections] = useState(loadExpandedSections);
  const [gridPreferences, setGridPreferences] = useState(() => parseGridPreferences());
  const [sectionHeights, setSectionHeights] = useState(() => parseSectionHeights());
  const [fullscreenSection, setFullscreenSection] = useState(null);

  const isOwnerLike = isManagement(user);
  const isNonManagement = !isOwnerLike;
  const canAccessRequests = canAccessRequestsPage(user);
  const canAccessJobUnlockRequests = canAccessJobUnlockRequestsTab(user);
  const canAccessPartRequests = canAccessPartRequestsTab(user);
  const canAccessProposals = canAccessProposalsTab(user);

  const requestsChipSx = (bgColor, textColor, borderColor) => ({
    backgroundColor: bgColor,
    color: textColor,
    fontSize: '0.75rem',
    fontWeight: 500,
    height: '24px',
    borderRadius: '6px',
    border: `1px solid ${borderColor}`,
    '& .MuiChip-label': {
      padding: '0 8px',
    },
  });

  const renderRequestsClientChip = (params) => {
    const clientId = params.row.client_id;
    const label = params.value || 'Unknown';
    const { bgColor, textColor, borderColor } = getClientChipStyles(clientId);
    return (
      <Chip
        label={label}
        sx={requestsChipSx(bgColor, textColor, borderColor)}
      />
    );
  };

  const renderRequestsJobStatusChip = (params) => {
    const statusId = Number(params.value);
    const label = getStatusLabel(statusId);
    const { bgColor, textColor, borderColor } = getStatusChipStyles(statusId);
    return (
      <Chip
        label={label}
        sx={requestsChipSx(bgColor, textColor, borderColor)}
      />
    );
  };

  const renderRequestsTechnicianStatusChip = (params) => {
    const statusId = Number(params.row.status);
    const label = params.value || 'Unknown';
    const { bgColor, textColor, borderColor } = getJobTechnicianChipStyles(statusId);
    return (
      <Chip
        label={label}
        sx={requestsChipSx(bgColor, textColor, borderColor)}
      />
    );
  };

  const columns = [
    {
      field: 'client_name',
      headerName: 'Client Name',
      width: 200,
      renderCell: renderRequestsClientChip,
    },
    { field: 'phone', headerName: 'Phone', width: 130 },
    { field: 'city', headerName: 'City', width: 100 },
    { field: 'state', headerName: 'State', width: 100 },
    { field: 'display_as', headerName: 'Display As', width: 100 },
    { field: 'client_address', headerName: 'Client Address', width: 150 },
    {
      field: 'email',
      headerName: 'Requested By',
      width: 160,
      valueFormatter: (value) => displayUserByEmail(value),
    },
    { field: 'request_notes', headerName: 'Request Notes', width: 180 },
    { field: 'review_notes', headerName: 'Review Notes', width: 180 },
    { 
      field: 'created_at', 
      headerName: 'Created At', 
      width: 110,
      valueFormatter: value => value ? value.slice(0, 10) : '',
    },
    { field: 'reviewed_at', headerName: 'Reviewed At', width: 180 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <RequestsActionButtons
          onAccept={() => handleOpenAcceptClientRequest(params.row)}
          onReject={() => handleOpenRejectClientRequestDialog(params.row)}
        />
      ),
    },
  ];

  // Open JobDetailsDialog as overlay (same page) - opens to technicians tab (index 1)
  const handleOpenJobOverlay = (row) => {
    if (!row?.job_id) return;
    const job = (jobs || []).find(j => j.id === row.job_id);
    if (job) {
      setInitialTabValue(1); // Technicians tab
      setSelectedJobForDetails(job);
    }
  };

  // Navigate to Jobs page and open JobDetailsDialog there
  const handleNavigateToJobsAndOpen = (row) => {
    if (!row?.job_id) return;
    navigate(routes.jobs, { state: { openJobId: row.job_id, openJobTab: 'technicians' } });
  };

  const technicianRequestColumns = [
    { field: 'work_order', headerName: 'Work Order', width: 140 },
    {
      field: 'client_name',
      headerName: 'Client',
      width: 180,
      renderCell: renderRequestsClientChip,
    },
    { field: 'job_description', headerName: 'Description', width: 200 },
    {
      field: 'dispatcher',
      headerName: 'Dispatcher',
      width: 160,
      valueFormatter: (value) => displayUserByEmail(value) || 'Not assigned',
    },
    { field: 'technician_name', headerName: 'Technician/Vendor', width: 200 },
    {
      field: 'status_label',
      headerName: 'Status',
      width: 150,
      renderCell: renderRequestsTechnicianStatusChip,
    },
    {
      field: 'scheduled_date',
      headerName: 'Visit Date',
      width: 120,
      valueFormatter: value => value ? String(value).slice(0, 10) : '',
    },
    {
      field: 'requested_unlock',
      headerName: 'Unlock?',
      width: 90,
      valueFormatter: value => (value ? 'Yes' : 'No'),
    },
    {
      field: 'attention_reasons_text',
      headerName: 'Reason(s)',
      width: 200,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <RequestsActionButtons
          onOpenHere={() => handleOpenJobOverlay(params.row)}
          onOpenInJobs={() => handleNavigateToJobsAndOpen(params.row)}
        />
      ),
    },
  ];

  const handleRowClick = (params) => {
    handleOpenAcceptClientRequest(params.row);
  };

  const handleOpenAcceptClientRequest = (row) => {
    if (!row?.id) return;
    setSelectedRequest(row);
    setAcceptClientRequestDialogOpen(true);
  };

  const handleOpenRejectClientRequestDialog = (row) => {
    setRejectClientRequestRow(row);
    setRejectClientRequestReason('');
  };

  const handleRejectClientRequest = async () => {
    if (!rejectClientRequestRow?.id || rejectingClientRequest) return;
    setRejectingClientRequest(true);
    try {
      const response = await POST(
        api.rejectClientRequest,
        {},
        {
          request_id: rejectClientRequestRow.id,
          reason: rejectClientRequestReason?.trim() || undefined,
        }
      );
      if (response?.error) {
        setDismissFeedback({
          open: true,
          severity: 'error',
          message: response.message || 'Failed to reject client request',
        });
      } else {
        dispatch(removeClientRequest(rejectClientRequestRow.id));
        setRejectClientRequestRow(null);
        setRejectClientRequestReason('');
        setDismissFeedback({
          open: true,
          severity: 'success',
          message: 'Client request rejected',
        });
      }
    } catch (err) {
      setDismissFeedback({
        open: true,
        severity: 'error',
        message: err?.message || 'Failed to reject client request',
      });
    } finally {
      setRejectingClientRequest(false);
    }
  };

  // Open JobDetailsDialog for job unlock requests - details tab (index 0) where Unlock lives
  const handleOpenJobUnlockOverlay = (row) => {
    if (!row?.job_id) return;
    const job = (jobs || []).find(j => j.id === row.job_id);
    if (job) {
      setInitialTabValue(0);
      setSelectedJobForDetails(job);
    }
  };

  const handleNavigateToJobUnlock = (row) => {
    if (!row?.job_id) return;
    navigate(routes.jobs, { state: { openJobId: row.job_id } });
  };

  const handleJobUnlockRowClick = (row) => {
    handleOpenJobUnlockOverlay(row);
  };

  const handleOpenDismissUnlockDialog = (row) => {
    setDismissUnlockRow(row);
    setDismissUnlockReason('');
  };

  const handleOpenUnlockJobDialog = (row) => {
    setUnlockJobRow(row);
  };

  const handleUnlockJobRequest = async () => {
    if (!unlockJobRow?.job_id || unlockingJob) return;
    setUnlockingJob(true);
    try {
      const response = await PATCH(
        `${api.unlockJob}/${unlockJobRow.job_id}/unlock`,
        {},
        {}
      );
      if (response?.error) {
        setDismissFeedback({
          open: true,
          severity: 'error',
          message: response.message || 'Failed to unlock job',
        });
        return;
      }
      if (!response?.data) return;

      const existing = (jobs || []).find((j) => j.id === unlockJobRow.job_id);
      // Merge list-enriched fields with unlock PATCH (job row only).
      dispatch(updateJob({ ...(existing || {}), ...response.data }));

      // Same as JobDetailsDialog: unlock uninvoices proposals; PATCH has no proposals.
      const full = await GET(`${api.getJobById}/${unlockJobRow.job_id}`);
      if (full?.status === 200 && full?.data) {
        dispatch(updateJob({ ...(existing || {}), ...full.data }));
      }

      setUnlockJobRow(null);
      setDismissFeedback({
        open: true,
        severity: 'success',
        message: 'Job unlocked',
      });
    } catch (err) {
      setDismissFeedback({
        open: true,
        severity: 'error',
        message: err?.message || 'Failed to unlock job',
      });
    } finally {
      setUnlockingJob(false);
    }
  };

  const handleDismissUnlockRequest = async () => {
    if (!dismissUnlockRow?.job_id || dismissingUnlockRequest) return;
    setDismissingUnlockRequest(true);
    try {
      const response = await POST(
        `${api.dismissUnlockRequest}/${dismissUnlockRow.job_id}/dismiss-unlock-request`,
        {},
        { reason: dismissUnlockReason?.trim() || undefined }
      );
      if (response?.error) {
        setDismissFeedback({
          open: true,
          severity: 'error',
          message: response.message || 'Failed to dismiss unlock request',
        });
      } else if (response?.data) {
        const existing = (jobs || []).find((j) => j.id === dismissUnlockRow.job_id);
        dispatch(updateJob({ ...(existing || {}), ...response.data }));
        setDismissUnlockRow(null);
        setDismissUnlockReason('');
        setDismissFeedback({
          open: true,
          severity: 'success',
          message: 'Unlock request dismissed',
        });
      }
    } catch (err) {
      setDismissFeedback({
        open: true,
        severity: 'error',
        message: err?.message || 'Failed to dismiss unlock request',
      });
    } finally {
      setDismissingUnlockRequest(false);
    }
  };

  // Open JobDetailsDialog for "My Requests" — pick the right tab based on category
  const handleOpenMyRequestOverlay = (row) => {
    if (!row?.job_id) return;
    const job = (jobs || []).find(j => j.id === row.job_id);
    if (!job) return;
    const cat = (row.request_category || '').toLowerCase();
    if (cat.startsWith('proposal')) setInitialTabValue(4);
    else if (cat.startsWith('part')) setInitialTabValue(2);
    else if (cat.startsWith('tech')) setInitialTabValue(1);
    else setInitialTabValue(0);
    setSelectedJobForDetails(job);
  };

  const handleNavigateMyRequest = (row) => {
    if (!row?.job_id) return;
    const cat = (row.request_category || '').toLowerCase();
    const tab = cat.startsWith('proposal') ? 'proposals'
      : cat.startsWith('part') ? 'parts'
      : cat.startsWith('tech') ? 'technicians'
      : undefined;
    navigate(routes.jobs, { state: { openJobId: row.job_id, ...(tab ? { openJobTab: tab } : {}) } });
  };

  const handleMyRequestRowClick = (row) => {
    handleOpenMyRequestOverlay(row);
  };

  // Row click opens overlay (default behavior)
  const handleTechnicianRequestRowClick = (row) => {
    handleOpenJobOverlay(row);
  };

  // Open JobDetailsDialog for proposal requests - opens to proposals tab (index 4)
  const handleOpenProposalRequestOverlay = (row) => {
    if (!row?.job_id) return;
    const job = (jobs || []).find(j => j.id === row.job_id);
    if (job) {
      setInitialTabValue(4); // Proposals tab
      setSelectedJobForDetails(job);
    }
  };

  // Navigate to Jobs page for proposal request - opens proposals tab
  const handleNavigateToProposalRequest = (row) => {
    if (!row?.job_id) return;
    navigate(routes.jobs, { state: { openJobId: row.job_id, openJobTab: 'proposals' } });
  };

  // Row click opens overlay for proposal requests
  const handleProposalRequestRowClick = (row) => {
    handleOpenProposalRequestOverlay(row);
  };

  // Open JobDetailsDialog for part requests - opens to Parts tab (index 2)
  const handleOpenPartRequestOverlay = (row) => {
    if (!row?.job_id) return;
    const job = (jobs || []).find(j => j.id === row.job_id);
    if (job) {
      setInitialTabValue(2); // Parts tab
      setSelectedJobForDetails(job);
    }
  };

  // Navigate to Jobs page for part request - opens Parts tab
  const handleNavigateToPartRequest = (row) => {
    if (!row?.job_id) return;
    navigate(routes.jobs, { state: { openJobId: row.job_id, openJobTab: 'parts' } });
  };

  const handlePartRequestRowClick = (row) => {
    handleOpenPartRequestOverlay(row);
  };

  // Filter client requests based on search query
  const filteredClientRequests = useMemo(() => {
    if (!clientSearchQuery) return clientRequests;
    const query = clientSearchQuery.toLowerCase();
    return clientRequests.filter(request => 
      request.client_name?.toLowerCase().includes(query) ||
      request.phone?.toLowerCase().includes(query) ||
      request.city?.toLowerCase().includes(query) ||
      request.state?.toLowerCase().includes(query) ||
      request.email?.toLowerCase().includes(query) ||
      resolveUserDisplayNameByEmail(request.email, usersList).toLowerCase().includes(query) ||
      request.display_as?.toLowerCase().includes(query)
    );
  }, [clientRequests, clientSearchQuery, usersList]);

  const myRequestRows = useMemo(() => {
    if (!isNonManagement) return [];
    return deriveMyRequestRows(jobs || [], user);
  }, [jobs, user, isNonManagement]);

  const filteredMyRequestRows = useMemo(() => {
    if (!myRequestsSearchQuery) return myRequestRows;
    const query = myRequestsSearchQuery.toLowerCase();
    return myRequestRows.filter(row =>
      row.work_order?.toLowerCase().includes(query) ||
      row.client_name?.toLowerCase().includes(query) ||
      row.description?.toLowerCase().includes(query) ||
      row.request_category?.toLowerCase().includes(query) ||
      row.request_detail?.toLowerCase().includes(query) ||
      row.status_label?.toLowerCase().includes(query) ||
      row.dispatcher?.toLowerCase().includes(query) ||
      resolveUserDisplayNameByEmail(row.dispatcher, usersList).toLowerCase().includes(query) ||
      (!row.dispatcher && 'not assigned'.includes(query))
    );
  }, [myRequestRows, myRequestsSearchQuery, usersList]);

  const jobUnlockRequestRows = useMemo(() => {
    return deriveJobUnlockRequestRows(jobs || []);
  }, [jobs]);

  const filteredJobUnlockRequestRows = useMemo(() => {
    if (!jobUnlockSearchQuery) return jobUnlockRequestRows;
    const query = jobUnlockSearchQuery.toLowerCase();
    return jobUnlockRequestRows.filter(row =>
      row.work_order?.toLowerCase().includes(query) ||
      row.client_name?.toLowerCase().includes(query) ||
      row.description?.toLowerCase().includes(query) ||
      row.status_label?.toLowerCase().includes(query) ||
      row.locked_reason_label?.toLowerCase().includes(query) ||
      row.requested_unlock_reason?.toLowerCase().includes(query) ||
      resolveUserDisplayNameById(row.requested_unlock_by, usersList).toLowerCase().includes(query) ||
      row.dispatcher?.toLowerCase().includes(query) ||
      resolveUserDisplayNameByEmail(row.dispatcher, usersList).toLowerCase().includes(query) ||
      (!row.dispatcher && 'not assigned'.includes(query))
    );
  }, [jobUnlockRequestRows, jobUnlockSearchQuery, usersList]);

  const technicianRequestRows = useMemo(() => {
    return deriveJobTechnicianRequestRows(jobs || []);
  }, [jobs]);

  const filteredTechnicianRequestRows = useMemo(() => {
    if (!technicianSearchQuery) return technicianRequestRows;
    const query = technicianSearchQuery.toLowerCase();
    return technicianRequestRows.filter(row =>
      row.work_order?.toLowerCase().includes(query) ||
      row.client_name?.toLowerCase().includes(query) ||
      row.job_description?.toLowerCase().includes(query) ||
      row.technician_name?.toLowerCase().includes(query) ||
      row.status_label?.toLowerCase().includes(query) ||
      row.attention_reasons_text?.toLowerCase().includes(query) ||
      row.dispatcher?.toLowerCase().includes(query) ||
      resolveUserDisplayNameByEmail(row.dispatcher, usersList).toLowerCase().includes(query) ||
      (!row.dispatcher && 'not assigned'.includes(query))
    );
  }, [technicianRequestRows, technicianSearchQuery, usersList]);

  const proposalRequestRows = useMemo(() => {
    return deriveProposalRequestRows(jobs || []);
  }, [jobs]);

  const partRequestRows = useMemo(() => {
    return derivePartRequestRows(jobs || []);
  }, [jobs]);

  const filteredPartRequestRows = useMemo(() => {
    if (!partRequestSearchQuery) return partRequestRows;
    const query = partRequestSearchQuery.toLowerCase();
    return partRequestRows.filter(row =>
      row.work_order?.toLowerCase().includes(query) ||
      row.client_name?.toLowerCase().includes(query) ||
      row.part_name?.toLowerCase().includes(query) ||
      row.job_description?.toLowerCase().includes(query) ||
      row.request_type?.toLowerCase().includes(query) ||
      row.requested_by?.toLowerCase().includes(query) ||
      resolveUserDisplayNameByEmail(row.requested_by, usersList).toLowerCase().includes(query) ||
      row.dispatcher?.toLowerCase().includes(query) ||
      resolveUserDisplayNameByEmail(row.dispatcher, usersList).toLowerCase().includes(query) ||
      (!row.dispatcher && 'not assigned'.includes(query))
    );
  }, [partRequestRows, partRequestSearchQuery, usersList]);

  const filteredProposalRequestRows = useMemo(() => {
    if (!proposalSearchQuery) return proposalRequestRows;
    const query = proposalSearchQuery.toLowerCase();
    return proposalRequestRows.filter(row =>
      row.work_order?.toLowerCase().includes(query) ||
      row.client_name?.toLowerCase().includes(query) ||
      row.job_description?.toLowerCase().includes(query) ||
      row.proposal_text?.toLowerCase().includes(query) ||
      row.request_type?.toLowerCase().includes(query) ||
      row.requested_by?.toLowerCase().includes(query) ||
      resolveUserDisplayNameByEmail(row.requested_by, usersList).toLowerCase().includes(query) ||
      row.dispatcher?.toLowerCase().includes(query) ||
      resolveUserDisplayNameByEmail(row.dispatcher, usersList).toLowerCase().includes(query) ||
      (!row.dispatcher && 'not assigned'.includes(query))
    );
  }, [proposalRequestRows, proposalSearchQuery, usersList]);

  const myRequestColumns = [
    { field: 'work_order', headerName: 'Work Order', width: 140 },
    { field: 'request_category', headerName: 'Type', width: 170 },
    { field: 'request_detail', headerName: 'Detail', width: 220 },
    {
      field: 'requested_at',
      headerName: 'Requested At',
      width: 120,
      valueFormatter: value => value ? String(value).slice(0, 10) : '',
    },
    {
      field: 'client_name',
      headerName: 'Client',
      width: 180,
      renderCell: renderRequestsClientChip,
    },
    { field: 'description', headerName: 'Description', width: 200 },
    {
      field: 'dispatcher',
      headerName: 'Dispatcher',
      width: 160,
      valueFormatter: (value) => displayUserByEmail(value) || 'Not assigned',
    },
    {
      field: 'status',
      headerName: 'Job Status',
      width: 150,
      renderCell: renderRequestsJobStatusChip,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <RequestsActionButtons
          onOpenHere={() => handleOpenMyRequestOverlay(params.row)}
          onOpenInJobs={() => handleNavigateMyRequest(params.row)}
        />
      ),
    },
  ];

  const proposalRequestColumns = [
    { field: 'work_order', headerName: 'Work Order', width: 140 },
    {
      field: 'client_name',
      headerName: 'Client',
      width: 180,
      renderCell: renderRequestsClientChip,
    },
    { field: 'job_description', headerName: 'Description', width: 200 },
    {
      field: 'dispatcher',
      headerName: 'Dispatcher',
      width: 160,
      valueFormatter: (value) => displayUserByEmail(value) || 'Not assigned',
    },
    { field: 'proposal_text', headerName: 'Proposal', width: 200 },
    {
      field: 'price',
      headerName: 'Price',
      width: 100,
      valueFormatter: value => value ? `$${Number(value).toLocaleString()}` : '$0',
    },
    { field: 'request_type', headerName: 'Request Type', width: 140 },
    {
      field: 'requested_by',
      headerName: 'Requested By',
      width: 180,
      valueFormatter: (value) => displayUserByEmail(value),
    },
    {
      field: 'requested_at',
      headerName: 'Requested At',
      width: 120,
      valueFormatter: value => value ? String(value).slice(0, 10) : '',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <RequestsActionButtons
          onOpenHere={() => handleOpenProposalRequestOverlay(params.row)}
          onOpenInJobs={() => handleNavigateToProposalRequest(params.row)}
        />
      ),
    },
  ];

  const jobUnlockRequestColumns = [
    { field: 'work_order', headerName: 'Work Order', width: 140 },
    {
      field: 'requested_unlock_reason',
      headerName: 'Reason',
      width: 220,
    },
    {
      field: 'requested_unlock_by',
      headerName: 'Requested By',
      width: 180,
      valueFormatter: (value) => resolveUserDisplayNameById(value, usersList),
    },
    {
      field: 'requested_unlock_at',
      headerName: 'Requested At',
      width: 120,
      valueFormatter: value => value ? String(value).slice(0, 10) : '',
    },
    {
      field: 'client_name',
      headerName: 'Client',
      width: 180,
      renderCell: renderRequestsClientChip,
    },
    { field: 'description', headerName: 'Description', width: 200 },
    {
      field: 'dispatcher',
      headerName: 'Dispatcher',
      width: 160,
      valueFormatter: (value) => displayUserByEmail(value) || 'Not assigned',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      renderCell: renderRequestsJobStatusChip,
    },
    {
      field: 'locked_reason_label',
      headerName: 'Locked Reason',
      width: 150,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 380,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <RequestsActionButtons
          onOpenHere={() => handleOpenJobUnlockOverlay(params.row)}
          onOpenInJobs={() => handleNavigateToJobUnlock(params.row)}
          onUnlock={() => handleOpenUnlockJobDialog(params.row)}
          onDismiss={() => handleOpenDismissUnlockDialog(params.row)}
        />
      ),
    },
  ];

  const partRequestColumns = [
    { field: 'work_order', headerName: 'Work Order', width: 140 },
    {
      field: 'client_name',
      headerName: 'Client',
      width: 180,
      renderCell: renderRequestsClientChip,
    },
    { field: 'part_name', headerName: 'Part', width: 180 },
    { field: 'job_description', headerName: 'Description', width: 200 },
    {
      field: 'dispatcher',
      headerName: 'Dispatcher',
      width: 160,
      valueFormatter: (value) => displayUserByEmail(value) || 'Not assigned',
    },
    {
      field: 'cost',
      headerName: 'Cost',
      width: 100,
      valueFormatter: value => value != null ? `$${Number(value).toLocaleString()}` : '$0',
    },
    { field: 'request_type', headerName: 'Request Type', width: 160 },
    {
      field: 'requested_by',
      headerName: 'Requested By',
      width: 180,
      valueFormatter: (value) => displayUserByEmail(value),
    },
    {
      field: 'requested_at',
      headerName: 'Requested At',
      width: 120,
      valueFormatter: value => value ? String(value).slice(0, 10) : '',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <RequestsActionButtons
          onOpenHere={() => handleOpenPartRequestOverlay(params.row)}
          onOpenInJobs={() => handleNavigateToPartRequest(params.row)}
        />
      ),
    },
  ];

  // Handler for expanding/collapsing sections
  const handleSectionChange = (section) => (event, isExpanded) => {
    setExpandedSections(prev => {
      const updated = {
        ...prev,
        [section]: isExpanded
      };
      // Save to localStorage
      try {
        localStorage.setItem('requestsSectionsExpanded', JSON.stringify(updated));
      } catch (error) {
        console.error('Error saving expanded sections to localStorage:', error);
      }
      return updated;
    });
  };

  const sectionColumnFields = useMemo(() => ({
    myRequests: myRequestColumns.map((column) => column.field),
    clientRequests: columns.map((column) => column.field),
    jobUnlockRequests: jobUnlockRequestColumns.map((column) => column.field),
    technicianRequests: technicianRequestColumns.map((column) => column.field),
    partRequests: partRequestColumns.map((column) => column.field),
    proposalRequests: proposalRequestColumns.map((column) => column.field),
  }), [
    // Field names are static; join keeps this stable across renderCell identity churn
    myRequestColumns.map((column) => column.field).join(','),
    columns.map((column) => column.field).join(','),
    jobUnlockRequestColumns.map((column) => column.field).join(','),
    technicianRequestColumns.map((column) => column.field).join(','),
    partRequestColumns.map((column) => column.field).join(','),
    proposalRequestColumns.map((column) => column.field).join(','),
  ]);

  const sanitizedGridPreferences = useMemo(() => (
    Object.entries(sectionColumnFields).reduce((acc, [sectionKey, fields]) => {
      if (!fields.length) return acc;
      acc[sectionKey] = sanitizeGridPreference(
        gridPreferences?.[sectionKey] ?? DEFAULT_GRID_PREFERENCE,
        fields,
      );
      return acc;
    }, {})
  ), [gridPreferences, sectionColumnFields]);

  useEffect(() => {
    window.localStorage.setItem(
      GRID_PREFERENCES_STORAGE_KEY,
      JSON.stringify(sanitizedGridPreferences),
    );
  }, [sanitizedGridPreferences]);

  useEffect(() => {
    window.localStorage.setItem(SECTION_HEIGHTS_STORAGE_KEY, JSON.stringify(sectionHeights));
  }, [sectionHeights]);

  const updateSectionGridPreference = useCallback((sectionKey, updater) => {
    setGridPreferences((prev) => {
      const fields = sectionColumnFields[sectionKey] || [];
      const currentPreference = sanitizeGridPreference(
        prev?.[sectionKey] ?? DEFAULT_GRID_PREFERENCE,
        fields,
      );
      const nextPreference = typeof updater === 'function' ? updater(currentPreference) : updater;
      return {
        ...prev,
        [sectionKey]: sanitizeGridPreference(nextPreference, fields),
      };
    });
  }, [sectionColumnFields]);

  const handleSectionHeightChange = useCallback((sectionKey, nextHeight) => {
    setSectionHeights((prev) => ({
      ...prev,
      [sectionKey]: clampSectionHeight(nextHeight),
    }));
  }, []);

  const handleToggleFullscreen = useCallback((sectionKey) => {
    setFullscreenSection((prev) => (prev === sectionKey ? null : sectionKey));
  }, []);

  // Common search field styles
  const searchFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 1.5,
      backgroundColor: 'background.paper',
      fontSize: '0.875rem',
      height: '36px',
      '& input::placeholder': {
        color: 'text.secondary',
        opacity: 1,
      },
      '& fieldset': {
        borderColor: isDarkMode ? '#334155' : '#e0e0e0',
        borderWidth: '1px',
      },
      '&:hover fieldset': {
        borderColor: isDarkMode ? '#475569' : '#e0e0e0',
      },
      '&.Mui-focused fieldset': {
        borderColor: isDarkMode ? '#475569' : '#e0e0e0',
        borderWidth: '1px',
      },
    }
  };

  if (!canAccessRequests) {
    return (
      <Box sx={{
        color: 'text.primary',
        bgcolor: 'background.paper',
        padding: '24px',
        borderRadius: '8px',
        margin: '40px auto',
        maxWidth: '400px',
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: '1.2rem',
        boxShadow: isDarkMode ? '0 2px 8px rgba(211,47,47,0.3)' : '0 2px 8px rgba(211,47,47,0.15)'
      }}>
        You are not authorized to view this page
      </Box>
    );
  }

  return (
    <>
      <Paper 
        elevation={0}
        sx={{ 
          height: '100%', 
          width: '100%', 
          padding: { xs: 1, md: 3 },
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          overflow: 'hidden',
        }}
      >
        {/* Header with title */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1, flexShrink: 0 }}>
          <Typography 
            variant="h4" 
            component="h1"
            sx={{ 
              fontWeight: 700,
              fontSize: { xs: '1.25rem', md: '2rem' },
              color: 'text.primary',
            }}
          >
            Requests
          </Typography>
        </Box>

        {/* Grids */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            flex: 1,
            minHeight: 0,
            overflowY: fullscreenSection ? 'hidden' : 'auto',
          }}
        >
          {isNonManagement && (
            <RequestsSection
              sectionKey="myRequests"
              title="My Requests"
              rows={filteredMyRequestRows || []}
              columns={myRequestColumns}
              searchPlaceholder="Search by work order, type, client..."
              searchQuery={myRequestsSearchQuery}
              onSearchChange={setMyRequestsSearchQuery}
              searchFieldSx={searchFieldSx}
              expanded={expandedSections.myRequests}
              onExpandedChange={handleSectionChange('myRequests')}
              height={sectionHeights.myRequests ?? DEFAULT_SECTION_HEIGHT}
              onHeightChange={(nextHeight) => handleSectionHeightChange('myRequests', nextHeight)}
              isFullscreen={fullscreenSection === 'myRequests'}
              onToggleFullscreen={handleToggleFullscreen}
              hidden={Boolean(fullscreenSection && fullscreenSection !== 'myRequests')}
              gridPreference={sanitizedGridPreferences.myRequests}
              onUpdateGridPreference={(updater) => updateSectionGridPreference('myRequests', updater)}
              isDarkMode={isDarkMode}
              isMobile={isMobile}
              onRowClick={(params) => handleMyRequestRowClick(params.row)}
            />
          )}

          {isOwnerLike && (
            <RequestsSection
              sectionKey="clientRequests"
              title="Client Requests"
              rows={filteredClientRequests || []}
              columns={columns}
              searchPlaceholder="Search by name, phone, city..."
              searchQuery={clientSearchQuery}
              onSearchChange={setClientSearchQuery}
              searchFieldSx={searchFieldSx}
              expanded={expandedSections.clientRequests}
              onExpandedChange={handleSectionChange('clientRequests')}
              height={sectionHeights.clientRequests ?? DEFAULT_SECTION_HEIGHT}
              onHeightChange={(nextHeight) => handleSectionHeightChange('clientRequests', nextHeight)}
              isFullscreen={fullscreenSection === 'clientRequests'}
              onToggleFullscreen={handleToggleFullscreen}
              hidden={Boolean(fullscreenSection && fullscreenSection !== 'clientRequests')}
              gridPreference={sanitizedGridPreferences.clientRequests}
              onUpdateGridPreference={(updater) => updateSectionGridPreference('clientRequests', updater)}
              isDarkMode={isDarkMode}
              isMobile={isMobile}
              onRowClick={handleRowClick}
              disableRowSelectionOnClick={false}
            />
          )}

          {canAccessJobUnlockRequests && (
            <RequestsSection
              sectionKey="jobUnlockRequests"
              title="Job Unlock Requests"
              rows={filteredJobUnlockRequestRows || []}
              columns={jobUnlockRequestColumns}
              searchPlaceholder="Search by work order, client, reason..."
              searchQuery={jobUnlockSearchQuery}
              onSearchChange={setJobUnlockSearchQuery}
              searchFieldSx={searchFieldSx}
              expanded={expandedSections.jobUnlockRequests}
              onExpandedChange={handleSectionChange('jobUnlockRequests')}
              height={sectionHeights.jobUnlockRequests ?? DEFAULT_SECTION_HEIGHT}
              onHeightChange={(nextHeight) => handleSectionHeightChange('jobUnlockRequests', nextHeight)}
              isFullscreen={fullscreenSection === 'jobUnlockRequests'}
              onToggleFullscreen={handleToggleFullscreen}
              hidden={Boolean(fullscreenSection && fullscreenSection !== 'jobUnlockRequests')}
              gridPreference={sanitizedGridPreferences.jobUnlockRequests}
              onUpdateGridPreference={(updater) => updateSectionGridPreference('jobUnlockRequests', updater)}
              isDarkMode={isDarkMode}
              isMobile={isMobile}
              onRowClick={(params) => handleJobUnlockRowClick(params.row)}
            />
          )}

          <RequestsSection
              sectionKey="technicianRequests"
              title="Technician Requests"
              rows={filteredTechnicianRequestRows || []}
              columns={technicianRequestColumns}
              searchPlaceholder="Search by work order, client, technician..."
              searchQuery={technicianSearchQuery}
              onSearchChange={setTechnicianSearchQuery}
              searchFieldSx={searchFieldSx}
              expanded={expandedSections.technicianRequests}
              onExpandedChange={handleSectionChange('technicianRequests')}
              height={sectionHeights.technicianRequests ?? DEFAULT_SECTION_HEIGHT}
              onHeightChange={(nextHeight) => handleSectionHeightChange('technicianRequests', nextHeight)}
              isFullscreen={fullscreenSection === 'technicianRequests'}
              onToggleFullscreen={handleToggleFullscreen}
              hidden={Boolean(fullscreenSection && fullscreenSection !== 'technicianRequests')}
              gridPreference={sanitizedGridPreferences.technicianRequests}
              onUpdateGridPreference={(updater) => updateSectionGridPreference('technicianRequests', updater)}
              isDarkMode={isDarkMode}
              isMobile={isMobile}
              onRowClick={(params) => handleTechnicianRequestRowClick(params.row)}
            />

          {canAccessPartRequests && (
            <RequestsSection
              sectionKey="partRequests"
              title="Parts Requests"
              rows={filteredPartRequestRows || []}
              columns={partRequestColumns}
              searchPlaceholder="Search by work order, client, part..."
              searchQuery={partRequestSearchQuery}
              onSearchChange={setPartRequestSearchQuery}
              searchFieldSx={searchFieldSx}
              expanded={expandedSections.partRequests}
              onExpandedChange={handleSectionChange('partRequests')}
              height={sectionHeights.partRequests ?? DEFAULT_SECTION_HEIGHT}
              onHeightChange={(nextHeight) => handleSectionHeightChange('partRequests', nextHeight)}
              isFullscreen={fullscreenSection === 'partRequests'}
              onToggleFullscreen={handleToggleFullscreen}
              hidden={Boolean(fullscreenSection && fullscreenSection !== 'partRequests')}
              gridPreference={sanitizedGridPreferences.partRequests}
              onUpdateGridPreference={(updater) => updateSectionGridPreference('partRequests', updater)}
              isDarkMode={isDarkMode}
              isMobile={isMobile}
              onRowClick={(params) => handlePartRequestRowClick(params.row)}
            />
          )}

          {canAccessProposals && (
            <RequestsSection
              sectionKey="proposalRequests"
              title="Job Proposal/Invoice"
              rows={filteredProposalRequestRows || []}
              columns={proposalRequestColumns}
              searchPlaceholder="Search by work order, client, proposal..."
              searchQuery={proposalSearchQuery}
              onSearchChange={setProposalSearchQuery}
              searchFieldSx={searchFieldSx}
              expanded={expandedSections.proposalRequests}
              onExpandedChange={handleSectionChange('proposalRequests')}
              height={sectionHeights.proposalRequests ?? DEFAULT_SECTION_HEIGHT}
              onHeightChange={(nextHeight) => handleSectionHeightChange('proposalRequests', nextHeight)}
              isFullscreen={fullscreenSection === 'proposalRequests'}
              onToggleFullscreen={handleToggleFullscreen}
              hidden={Boolean(fullscreenSection && fullscreenSection !== 'proposalRequests')}
              gridPreference={sanitizedGridPreferences.proposalRequests}
              onUpdateGridPreference={(updater) => updateSectionGridPreference('proposalRequests', updater)}
              isDarkMode={isDarkMode}
              isMobile={isMobile}
              onRowClick={(params) => handleProposalRequestRowClick(params.row)}
            />
          )}
        </Box>
      </Paper>
      
      <AcceptClientRequestDialog
        setAcceptClientRequestDialogOpen={setAcceptClientRequestDialogOpen}
        acceptClientRequestDialogOpen={acceptClientRequestDialogOpen}
        selectedRequest={selectedRequest}
      />

      <RejectClientRequestDialog
        open={Boolean(rejectClientRequestRow)}
        reason={rejectClientRequestReason}
        onReasonChange={setRejectClientRequestReason}
        loading={rejectingClientRequest}
        clientName={rejectClientRequestRow?.client_name}
        onClose={() => {
          if (rejectingClientRequest) return;
          setRejectClientRequestRow(null);
          setRejectClientRequestReason('');
        }}
        onConfirm={handleRejectClientRequest}
      />

      <DismissJobUnlockRequestDialog
        open={Boolean(dismissUnlockRow)}
        reason={dismissUnlockReason}
        onReasonChange={setDismissUnlockReason}
        loading={dismissingUnlockRequest}
        workOrder={dismissUnlockRow?.work_order}
        onClose={() => {
          if (dismissingUnlockRequest) return;
          setDismissUnlockRow(null);
          setDismissUnlockReason('');
        }}
        onConfirm={handleDismissUnlockRequest}
      />

      <UnlockJobConfirmDialog
        open={Boolean(unlockJobRow)}
        loading={unlockingJob}
        workOrder={unlockJobRow?.work_order}
        onClose={() => {
          if (unlockingJob) return;
          setUnlockJobRow(null);
        }}
        onConfirm={handleUnlockJobRequest}
      />

      <Snackbar
        open={dismissFeedback.open}
        autoHideDuration={4000}
        onClose={() => setDismissFeedback((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setDismissFeedback((prev) => ({ ...prev, open: false }))}
          severity={dismissFeedback.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {dismissFeedback.message}
        </Alert>
      </Snackbar>

      {/* Embedded JobDetailsDialog overlay for requests */}
      <JobDetailsDialog
        selectedJob={selectedJobForDetails}
        setSelectedJob={setSelectedJobForDetails}
        user={user}
        initialTabValue={initialTabValue}
      />
    </>
  );
};

export default Requests;